// React / Next
import { Link } from '@tanstack/react-router';

// Components
import { Typography } from '@archieai/ui';

// Utils
import { cn } from '../../utils/cn';

// Types
type FeaturedCardProps = {
  /** URL of the cover image */
  imageSrc: string;
  /** Alt text for the cover image */
  imageAlt?: string;
  /** Title of the featured post */
  title: string;
  /** Description/excerpt of the featured post */
  description: string;
  /** Author name */
  author: string;
  /** Tags or categories, pipe-separated (e.g., "AI Strategy | Future of Accounting") */
  tags: string;
  /** Link to the full post */
  href?: string;
  /** Additional className for custom styling */
  className?: string;
  /** Optional click handler for analytics tracking */
  onClick?: () => void;
};

export const FeaturedCard = ({
  imageSrc,
  imageAlt = '',
  title,
  description,
  author,
  tags,
  href,
  className,
  onClick,
}: FeaturedCardProps) => {
  const cardContent = (
    <>
      {/* Image Container */}
      <div className="w-[484px] h-[432px] shrink-0 max-lg:w-full max-lg:h-auto max-lg:aspect-[484/432]">
        <img
          src={imageSrc}
          alt={imageAlt}
          className="w-full h-full object-cover rounded-l-12 max-lg:rounded-t-12 max-lg:rounded-bl-none"
        />
      </div>

      {/* Content Container */}
      <div className="flex flex-col justify-between py-[var(--spacing-24)] pr-[var(--spacing-24)] max-lg:p-[var(--spacing-24)] pl-[var(--spacing-24)]">
        {/* Top Section: Label + Title + Description */}
        <div className="flex flex-col gap-[var(--spacing-16)]">
          {/* Featured Label */}
          <Typography
            variant="column-header"
            className="text-blue-800 uppercase tracking-wide"
          >
            Featured
          </Typography>

          {/* Title */}
          <h2 className="font-heading text-[40px] leading-[48px] tracking-[-0.5px] font-light text-orange-200 max-md:text-[32px] max-md:leading-[40px]">
            {title}
          </h2>

          {/* Description */}
          <Typography as="p" variant="small" className="text-orange-200">
            {description}
          </Typography>
        </div>

        {/* Bottom Section: Author + Tags */}
        <div className="flex flex-col gap-[var(--spacing-8)] mt-[var(--spacing-24)]">
          {/* Author */}
          <Typography as="p" variant="h4" className="text-blue-800">
            {author}
          </Typography>

          {/* Tags */}
          <Typography variant="x-small" className="text-blue-800">
            {tags}
          </Typography>
        </div>
      </div>
    </>
  );

  const baseClasses = cn(
    'flex bg-blue-1000 rounded-16 overflow-hidden w-full max-w-[1016px]',
    'max-lg:flex-col',
    'transition-shadow duration-200 hover:shadow-background',
    className
  );

  if (href) {
    return (
      <Link
        to={href}
        className={baseClasses}
        aria-label={`Read featured post: ${title}`}
        onClick={onClick}
      >
        {cardContent}
      </Link>
    );
  }

  return (
    <article className={baseClasses}>
      {cardContent}
    </article>
  );
};

